import { HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";
import * as Sentry from "@sentry/browser";
import { BrowserClient, makeFetchTransport } from "@sentry/browser";
import { BrowserClientOptions } from "@sentry/browser/types/client";
import { Event, EventHint } from "@sentry/types";
import { severityFromString } from "@sentry/utils";
import {
  IBreadcrumb,
  IContextDetails,
  IErrorDetails,
  IHint,
  ILoggerService,
} from "src/app/modules/error-handler/error-handler-interfaces.service";

/**
 * The default function used to filter events before they are sent to Sentry. This is called in the default beforeSend
 * method, and filters out `Non-Error exception`'s. When using a custom `beforeSend()` function in `ERROR_LOGGER_OPTIONS`,
 * `filterBeforeSend()` can be imported and called to apply the default filters.
 *
 * @param event The Sentry error event.
 * @param hint The Sentry hint.
 *
 * @example
 * providers: [{
 *   provide: 'ERROR_LOGGER_OPTIONS', useValue: {
 *     beforeSend: (event, hint) => filterBeforeSend(event, hint);
 *   }
 * }]
 */
export function filterBeforeSend(event: Event, hint: EventHint) {
  try {
    const _event = JSON.parse(JSON.stringify(event));
    if (
      event.message &&
      event.message.toString().startsWith("Non-Error exception captured") &&
      hint.originalException
    ) {
      // tslint:disable-next-line no-null-keyword
      return null;
    } else {
      if (hint && hint.originalException) {
        _event.extra.Hint = hint;
      }
      return _event;
    }
  } catch (error) {
    this.sentry.captureException(error);
  }
}

// We have to have @params here for intellisense and on the constructor for Compodoc.
/**
 * An `ngx-error-handler` logging adaptor wrapped around Sentry's library. Calls to this logger will send Sentry events.
 * To enable this, when constructing this class we override our default settings with any provided via `ERROR_LOGGER_OPTIONS`,
 * [create a new Sentry BrowserClient with the options](https://docs.sentry.io/platforms/javascript/advance-settings/), and
 * [bind it to the global Hub](https://github.com/getsentry/sentry-javascript/issues/1764#issuecomment-441584499).
 *
 * @param _errorLoggerOptions {Sentry.BrowserOptions} A logger options object, which will be passed to sentry. This includes details such as the Sentry `dsn`.
 * @param _sentryLibrary Used to allow for injecting a fake Sentry module during testing.
 */
@Injectable()
export class SentryLoggingService implements ILoggerService {
  /** @ignore A reference to the Sentry library. */
  sentryLoggerClient: Sentry.BrowserClient;
  /** @ignore A reference to the Sentry library/object in use. */
  sentry: any;

  /** Get the scope details for the bound Sentry hub the service is currently interacting with. */
  get currentHub() {
    return this.sentry.getCurrentHub();
  }

  constructor(
    /** A logger options object, which will be passed to sentry. This includes details such as the Sentry `dsn`. */
    @Inject("ERROR_LOGGER_OPTIONS")
    private _errorLoggerOptions: BrowserClientOptions,
    /** Used to allow for injecting a fake Sentry module during testing. */
    @Inject("SENTRY") @Optional() private _sentryLibrary?,
  ) {
    this.sentry = _sentryLibrary || Sentry;

    // If a beforeSend function is passed in, use that instead of our filter.
    const _beforeSend = _errorLoggerOptions.beforeSend
      ? _errorLoggerOptions.beforeSend
      : filterBeforeSend;

    const initOptions: BrowserClientOptions = {
      ...this._errorLoggerOptions,
      debug: true,
      beforeSend: _beforeSend,
      transport: makeFetchTransport,
      integrations: [
        new Sentry.Integrations.GlobalHandlers(),
        new Sentry.Integrations.TryCatch(),
        // new Sentry.Integrations.UserAgent(),
        new Sentry.Integrations.Breadcrumbs({
          dom: true,
          xhr: true,
          fetch: true,
          history: true,
          sentry: true,
          console: false,
        }),
      ],
      // Currently, Sentry seems to break when creating a breadcrumb from (our?) console.error. If console crumbs are enabled, use this.
      // , beforeBreadcrumb: (breadcrumb, hint?) => {
      //   if (breadcrumb.category === 'console' && breadcrumb.level === 'error') {
      //     console.log('BLOCKING ERROR BREADCRUMB', breadcrumb, 'Hint:', hint);
      //     return;
      //   }
      //   return breadcrumb;
      // }
    };

    this.initService(initOptions);
  }

  /** @ignore Binds the client options to the global Sentry so any following events use them. */
  initService = (options: BrowserClientOptions) => {
    const client = new BrowserClient(options);
    this.currentHub.bindClient(client);
  };

  /**
   * A function that sends (context)[https://docs.sentry.io/platforms/javascript/?platform=node#context] details to
   * the logger so that they can be attached to any successive errors.
   * @param context An object containing the new properties to set on the application error context.
   */
  configureContext = (context: IContextDetails) => {
    this.currentHub.configureScope((scope) => {
      if (context.level) {
        const severity = severityFromString(context.level.toLowerCase());
        if (severity) {
          scope.setLevel(severity);
        } else {
          console.error("Invalid level provided:", context.level);
        }
      }

      if (context.tags) {
        Object.keys(context.tags).forEach((tag) => {
          scope.setTag(tag, context.tags[tag]);
        });
      }
      if (context.extra) {
        Object.keys(context.extra).forEach((key) => {
          scope.setExtra(key, context.extra[key]);
        });
      }
      if (context.user) {
        scope.setUser(context.user);
      }
      if (context.fingerprint) {
        scope.setFingerprint(context.fingerprint);
      }
    });
  };

  /**
   * A function for record details to help identify events leading up to an error.
   * @param details Information about the event. The `message` key is the most commonly used.
   * @param hint Typically this hint holds the original exception?
   */
  logBreadcrumb = (details: IBreadcrumb, hint?: IHint): void => {
    this.currentHub.addBreadcrumb(details as Sentry.Breadcrumb, hint);
  };

  /**
   * A function for logging an error/exception to Sentry. Additionally, Sentry (context)[https://docs.sentry.io/platforms/javascript/?platform=node#context]
   * details are sent with the error by using [withScope](https://docs.sentry.io/enriching-error-data/scopes/?platform=node#local-scopes)
   * to create a local scope.
   * @param error The error object to send to Sentry.
   * @param errDetails An object with details about the error to be logged.
   * @param message A message to be added to the logged error. It will appear as extra information in the sentry error.
   * @return The generated event ID.
   */
  // logError = (error?: Error | HttpErrorResponse, errorDetails?: IErrorDetails, message?: string) => any;
  logError = (
    error: Error | HttpErrorResponse,
    errorDetails: IErrorDetails = {},
  ): any => {
    let _errorId: string;
    this.sentry.withScope((scope: Sentry.Scope) => {
      // Set the error level.
      if (errorDetails.level) {
        const severity = severityFromString(errorDetails.level);
        if (severity) {
          scope.setLevel(severity);
        } else {
          console.error("Invalid level provided:", errorDetails.level);
        }
      }
      // Set any provided custom tags.
      if (errorDetails.tags) {
        Object.keys(errorDetails.tags).forEach((tag) => {
          scope.setTag(tag, errorDetails.tags[tag]);
        });
      }
      // Set any provided custom fields.
      if (errorDetails.extra) {
        Object.keys(errorDetails.extra).forEach((key) => {
          scope.setExtra(key, errorDetails.extra[key]);
        });
      }
      // Set the user scope if available.
      if (errorDetails.user) {
        scope.setUser(errorDetails.user);
      }
      // Set the fingerprint if available.
      if (errorDetails.fingerprint) {
        scope.setFingerprint(errorDetails.fingerprint);
      }

      _errorId = this.currentHub.captureException(error);
    });
    return _errorId;
  };

  /**
   * A function for manually capturing a custom event object.
   * @param message The message text.
   */
  logEvent = (event: string): void => {
    this.currentHub.captureEvent(event as Sentry.Event);
  };

  /**
   * A function for capturing a bare message/textual information to Sentry, leveraging Sentry's [captureMessage()](https://docs.sentry.io/clients/go/usage/#capturing-messages) .
   * @param message The message text.
   */
  logMessage = (message: string): void => {
    this.currentHub.captureMessage(message);
  };
}
